import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AuthState, JWTPayload } from "@models/auth.model";
import { jwtDecode } from "jwt-decode";
import { toast } from "keep-react";
import { t } from "i18next";
import { ROUTES } from "@/constants/routes";

/**
 * Sets the auth token in localStorage or sessionStorage based on `rememberMe` flag.
 * Always sets the auth_token cookie as it's technically necessary for middleware authentication.
 * Only non-essential cookies are controlled by cookiesAccepted (GDPR compliance).
 */
const setAuthToken = (token: string, rememberMe: boolean) => {
  if (rememberMe) {
    localStorage.setItem("auth_token", token);
    // Always set auth_token cookie - it's technically necessary for authentication
    document.cookie = `auth_token=${token}; path=/; max-age=${
      30 * 24 * 60 * 60
    }`;
  } else {
    sessionStorage.setItem("auth_token", token);
    // Always set session auth_token cookie - it's technically necessary for authentication
    document.cookie = `auth_token=${token}; path=/;`;
  }
  
  // Example: Optional tracking/analytics cookies would be controlled by cookiesAccepted
  // if (cookiesAccepted) {
  //   document.cookie = `analytics_id=${someId}; path=/; max-age=${30 * 24 * 60 * 60}`;
  // }
};

/**
 * Clears all application storage (auth tokens, local storage, session storage, cookies).
 */
const clearAllAppData = () => {
  // Auth-Token aus localStorage und sessionStorage entfernen
  localStorage.removeItem("auth_token");
  sessionStorage.removeItem("auth_token");
  localStorage.removeItem("mail");
  
  // Auth-Cookie löschen
  document.cookie = "auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  
  // Alle anderen anwendungsspezifischen localStorage-Einträge löschen
  const keysToKeep: string[] = [
    'khg-dev-mode', // Entwicklermodus-Status - WICHTIG: NIEMALS LÖSCHEN!
    'persist:cookieConsent' // Cookie-Consent-Entscheidung - muss unabhängig vom Login-Status bestehen bleiben
  ]; // Schlüssel, die nicht gelöscht werden sollen
  
  // Alle localStorage-Einträge durchgehen und löschen
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && !keysToKeep.includes(key)) {
      localStorage.removeItem(key);
      i--; // Index anpassen, da ein Element entfernt wurde
    }
  }
  
  // Alle sessionStorage-Einträge löschen
  sessionStorage.clear();
  
  // Alle Cookies löschen (außer die, die erhalten bleiben sollen)
  const cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
    document.cookie = name + "=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  }
};

/**
 * Validates a JWT token for expiration and required structure.
 */
const isTokenValid = (token: string): boolean => {
  try {
    const decoded = jwtDecode<JWTPayload>(token);

    const currentTime = Date.now() / 1000;
    return decoded.exp > currentTime && !!decoded.userId;
  } catch {
    return false;
  }
};

const initialState: AuthState = {
  userId: null,
  token: null,
  isAuthenticated: false,
  error: null,
  lastLogin: null,
  sessionExpiry: null,
  loginAttempts: 0,
  rememberMe: false,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{
        jwt: string;
        user_id: string;
        rememberMe: boolean;
        cookiesAccepted?: boolean;
      }>
    ) => {
      const { jwt, user_id, rememberMe } = action.payload;

      if (!isTokenValid(jwt)) {
        state.error = {
          code: "AUTH_ERROR",
          message: t("auth-errors.login-failed"),
          timestamp: new Date().toISOString(),
          authFailureReason: "invalid_token",
        };
        state.userId = null;
        state.token = null;
        state.isAuthenticated = false;
        state.loginAttempts += 1;
        toast.error(t("auth-errors.login-failed"));
        return;
      }

      try {
        const decoded = jwtDecode<JWTPayload>(jwt);
        state.userId = user_id;
        state.token = jwt;
        state.isAuthenticated = true;
        state.error = null;
        state.lastLogin = new Date().toISOString();
        state.sessionExpiry = new Date(decoded.exp * 1000).toISOString();
        state.loginAttempts = 0;
        state.rememberMe = rememberMe;
        setAuthToken(jwt, rememberMe);
        toast.success(t("auth.success"));
      } catch (error) {
        state.error = {
          code: "AUTH_ERROR",
          message: t("auth.error"),
          timestamp: new Date().toISOString(),
          details: { error: (error as Error).message },
        };
        state.userId = null;
        state.token = null;
        state.isAuthenticated = false;
        state.loginAttempts += 1;
        toast.error(t("auth.error"));
      }
    },
    logout: (state) => {
      // Vollständiges Zurücksetzen des Redux-States
      Object.assign(state, initialState);
      
      // Alle App-Daten aus dem Browser löschen
      clearAllAppData();
      
      // Erfolgsmeldung anzeigen
      toast.info(t("auth.logedOut"));
      
      // Optional: Zur Anmeldeseite weiterleiten
      if (typeof window !== 'undefined') {
        window.location.href = ROUTES.myAccount;
      }
    },
    reset: (state) => {
      Object.assign(state, {
        ...initialState,
        loginAttempts: state.loginAttempts,
      });
      clearAllAppData();
    },
    clearErrors: (state) => {
      state.error = null;
    },
    resetLoginAttempts: (state) => {
      state.loginAttempts = 0;
    },
    initializeAuth: (state) => {
      try {
        const token =
          localStorage.getItem("auth_token") ||
          sessionStorage.getItem("auth_token") ||
          document.cookie
            .split("; ")
            .find((row) => row.startsWith("auth_token="))
            ?.split("=")[1];
        if (token && isTokenValid(token)) {
          const decoded = jwtDecode<JWTPayload>(token);
          state.token = token;
          state.userId = decoded.userId;
          state.isAuthenticated = true;
          state.error = null;
          state.sessionExpiry = new Date(decoded.exp * 1000).toISOString();
        } else {
          clearAllAppData();
          state.isAuthenticated = false;
          state.error = null;
        }
      } catch (error) {
        state.error = {
          code: "AUTH_ERROR",
          message: t("auth.error"),
          timestamp: new Date().toISOString(),
          details: { error: (error as Error).message },
        };
      }
    },
  },
});

export const {
  setCredentials,
  logout,
  reset,
  clearErrors,
  resetLoginAttempts,
  initializeAuth,
} = authSlice.actions;

export const authReducer = authSlice.reducer;
